import type { LandingCampaign } from '@/types/landing'
import { mixHex, normalizeHexColor } from './campaign-winner-page-theme'

type CampaignColors = Pick<
  LandingCampaign,
  'primary_color' | 'secondary_color' | 'button_color' | 'highlight_color'
>

function luminance(hex: string): number {
  const channels = [1, 3, 5].map((index) => {
    const value = Number.parseInt(hex.slice(index, index + 2), 16) / 255
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

function contrastText(hex: string): '#ffffff' | '#111827' {
  return luminance(hex) > 0.179 ? '#111827' : '#ffffff'
}

export function resolveParticipantTheme(campaign: CampaignColors) {
  const primary = normalizeHexColor(campaign.primary_color) ?? '#0f2a53'
  const secondary =
    normalizeHexColor(campaign.secondary_color) ?? mixHex(primary, '#ffffff', 0.92)
  const button = normalizeHexColor(campaign.button_color) ?? primary
  const highlight = normalizeHexColor(campaign.highlight_color) ?? button
  const action =
    luminance(highlight) < 0.18 ? mixHex(highlight, '#ffffff', 0.42) : highlight
  const deep = mixHex(primary, '#101722', 0.74)
  const ink = mixHex(primary, '#151b25', 0.82)

  return {
    primary,
    secondary,
    button,
    highlight,
    action,
    deep,
    ink,
    muted: mixHex(ink, '#66717b', 0.6),
    canvas: mixHex(secondary, '#ffffff', 0.93),
    surface: mixHex(secondary, '#ffffff', 0.96),
    soft: mixHex(secondary, '#ffffff', 0.84),
    border: mixHex(deep, '#ffffff', 0.8),
    accentSoft: mixHex(highlight, '#ffffff', 0.84),
    accentInk: mixHex(highlight, ink, 0.72),
    buttonSoft: mixHex(button, '#ffffff', 0.86),
    buttonEdge: mixHex(button, deep, 0.42),
    actionEdge: mixHex(action, deep, 0.42),
    onPrimary: contrastText(primary),
    onButton: contrastText(button),
    onHighlight: contrastText(highlight),
    onAction: contrastText(action),
  }
}

export function buildParticipantThemeCss(campaign: CampaignColors): string {
  const palette = resolveParticipantTheme(campaign)
  return `:root {
    --campaign-primary-color: ${palette.primary};
    --campaign-secondary-color: ${palette.secondary};
    --campaign-button-color: ${palette.button};
    --campaign-highlight-color: ${palette.highlight};
    --campaign-primary-color-dark: ${mixHex(palette.primary, '#000000', 0.18)};
    --campaign-secondary-color-dark: ${mixHex(palette.secondary, '#000000', 0.12)};
    --campaign-button-color-dark: ${mixHex(palette.button, '#000000', 0.18)};
    --campaign-highlight-color-dark: ${mixHex(palette.highlight, '#000000', 0.18)};
    --campaign-primary-shadow: color-mix(in srgb, ${palette.primary} 22%, transparent);
    --campaign-primary-shadow-strong: color-mix(in srgb, ${palette.primary} 28%, transparent);
    --campaign-primary-soft: color-mix(in srgb, ${palette.primary} 10%, transparent);
    --campaign-primary-border: ${palette.border};
    --campaign-button-shadow: color-mix(in srgb, ${palette.button} 26%, transparent);
    --campaign-button-border: color-mix(in srgb, ${palette.button} 48%, white);
    --campaign-button-ring: color-mix(in srgb, ${palette.button} 16%, transparent);
    --campaign-highlight-shadow: color-mix(in srgb, ${palette.highlight} 24%, transparent);
    --campaign-highlight-border: color-mix(in srgb, ${palette.highlight} 52%, white);
    --participant-primary: ${palette.primary};
    --participant-secondary: ${palette.secondary};
    --participant-button: ${palette.button};
    --participant-highlight: ${palette.highlight};
    --participant-action: ${palette.action};
    --participant-deep: ${palette.deep};
    --participant-ink: ${palette.ink};
    --participant-muted: ${palette.muted};
    --participant-canvas: ${palette.canvas};
    --participant-surface: ${palette.surface};
    --participant-soft: ${palette.soft};
    --participant-border: ${palette.border};
    --participant-accent-soft: ${palette.accentSoft};
    --participant-accent-ink: ${palette.accentInk};
    --participant-button-soft: ${palette.buttonSoft};
    --participant-button-edge: ${palette.buttonEdge};
    --participant-action-edge: ${palette.actionEdge};
    --participant-on-primary: ${palette.onPrimary};
    --participant-on-button: ${palette.onButton};
    --participant-on-highlight: ${palette.onHighlight};
    --participant-on-action: ${palette.onAction};
  }`
}
